import React, { useState, useRef } from 'react';
import { AttachFile } from '@mui/icons-material';
import { toast } from 'react-toastify';

interface UploadAnexoNovaTarefaProps {
  anexos: File[];
  setAnexos: React.Dispatch<React.SetStateAction<File[]>>;
  enviandoAnexos?: boolean;
}

const UploadAnexoNovaTarefa: React.FC<UploadAnexoNovaTarefaProps> = ({ anexos, setAnexos, enviandoAnexos = false }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Função para abrir o seletor de arquivos
  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  // Função para adicionar arquivos à lista
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const extensoesProibidas = ['.exe', '.bat', '.cmd', '.sh', '.php', '.js'];

    const novos: File[] = [];
    for (const file of Array.from(files)) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`O arquivo ${file.name} excede 10MB`);
        continue;
      }
      if (file.size === 0) {
        toast.error(`O arquivo ${file.name} está vazio`);
        continue;
      }
      const extensao = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (extensoesProibidas.includes(extensao)) {
        toast.error(`Arquivos com extensão ${extensao} não são permitidos`);
        continue;
      }
      novos.push(file);
    }

    if (novos.length > 0) {
      setAnexos([...anexos, ...novos]);
    }
    
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Função para remover um arquivo da lista
  const handleRemoveFile = (index: number) => {
    const novosAnexos = [...anexos];
    novosAnexos.splice(index, 1);
    setAnexos(novosAnexos);
  };

  // Função para formatar o tamanho do arquivo
  const formatarTamanhoArquivo = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-2">
        <label className="block text-sm font-medium text-gray-700">
          Anexos {enviandoAnexos && <span className="text-blue-500 ml-2">(Enviando...)</span>}
        </label>
        <button
          type="button"
          onClick={handleClickUpload}
          className={`flex items-center text-sm ${enviandoAnexos ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}`}
          disabled={enviandoAnexos}
        >
          <AttachFile className="h-4 w-4 mr-1" />
          Adicionar arquivo
        </button>
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
          disabled={enviandoAnexos}
        />
      </div>
      
      {anexos.length > 0 ? (
        <div className="border border-gray-200 rounded-md p-2 max-h-40 overflow-y-auto">
          <ul className="divide-y divide-gray-200">
            {anexos.map((file, index) => (
              <li key={index} className="py-2 flex justify-between items-center">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-700">{file.name}</span>
                  <span className="ml-2 text-xs text-gray-500">({formatarTamanhoArquivo(file.size)})</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  className={`${enviandoAnexos ? 'text-gray-400 cursor-not-allowed' : 'text-red-500 hover:text-red-700'}`}
                  disabled={enviandoAnexos}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-gray-500 italic">Nenhum anexo adicionado</p>
      )}
    </div>
  );
};

export default UploadAnexoNovaTarefa;
